/*
 * Hold a section back until its images have actually arrived, then fade the
 * whole thing in at once.
 *
 * Images decode one at a time, so a masthead of portraits pops in piecemeal.
 * Waiting for all of them and revealing together reads as one page appearing
 * rather than several things landing.
 *
 * Opt in with data-od-reveal on the section, and give it the two rules that
 * go with it. The classes are added here and never in Liquid, so a visitor
 * whose JavaScript failed sees the page immediately rather than a blank one —
 * the arming only ever happens on a page that can also unarm itself.
 *
 * There is always a deadline. A slow CDN, an image that 404s, a font that
 * never resolves: none of them may leave the page invisible.
 */
(function () {
  'use strict';

  var DEADLINE = 1500;

  function settle(image) {
    if (image.complete && image.naturalWidth > 0) {
      /* Decoded, not merely downloaded — decoding is what actually causes the
         pop, and a complete image can still be undecoded. */
      return image.decode ? image.decode().catch(function () {}) : Promise.resolve();
    }
    return new Promise(function (resolve) {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  }

  function arm(section) {
    if (!section || section.dataset.odRevealBound === 'true') return;
    section.dataset.odRevealBound = 'true';

    section.classList.add('is-reveal-armed');

    var shown = false;
    function show() {
      if (shown) return;
      shown = true;
      section.classList.remove('is-reveal-armed');
      section.classList.add('is-reveal-done');
    }

    var deadline = window.setTimeout(show, DEADLINE);

    var waiting = [].slice.call(section.querySelectorAll('img')).map(settle);

    /* Web fonts too: text reflowing after the fade is the same flinch as an
       image landing late, and document.fonts.ready is cheap to wait on. */
    if (document.fonts && document.fonts.ready) {
      waiting.push(document.fonts.ready.catch(function () {}));
    }

    Promise.all(waiting).then(function () {
      window.clearTimeout(deadline);
      /* Two frames: one for the browser to lay the decoded images out, one to
         paint them, so the fade starts from the finished picture. */
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(show);
      });
    });
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var found = scope.querySelectorAll('[data-od-reveal]');
    for (var i = 0; i < found.length; i += 1) arm(found[i]);
    if (scope !== document && scope.matches && scope.matches('[data-od-reveal]')) arm(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); }, { once: true });
  } else {
    scan(document);
  }

  /* The theme swaps #MainContent rather than reloading. The event fires
     synchronously once the new markup is in, so arming here still lands
     before the browser paints it. */
  document.addEventListener('od:main-content-replaced', function () { scan(document); });
  document.addEventListener('shopify:section:load', function (event) { scan(event.target || document); });

  /* Belt and braces: a section inserted by something that does not announce
     itself still gets picked up. */
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m += 1) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n += 1) {
          var node = added[n];
          if (node && node.nodeType === 1) scan(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
